import React from 'react';
import { PAGE_CHROME_LOADER_BRAND_GREEN } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';

/** Tailwind `gray-900` — readable on lime; matches iOS session gate tint. */
const SPINNER_ON_GREEN = '#111827';

/** Light spokes for inline spinners on dark UIs. */
const SPINNER_ON_DARK = '#f3f4f6';

const BAR_COUNT = 12;

interface LoadingSpinnerProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
  /** When fullScreen: fills status strip / theme-color (default: brand green). Use light for logged-out boot. */
  statusBarBackground?: string;
}

/**
 * Classic UIActivityIndicator-style spinner: 12 short rounded bars in a ring with a fading “tail”.
 */
function IOSLineActivitySpinner({ pixelSize, fill }: { pixelSize: number; fill: string }) {
  const durationSec = 1;

  return (
    <svg
      width={pixelSize}
      height={pixelSize}
      viewBox="0 0 24 24"
      className="shrink-0 overflow-visible"
      aria-hidden
    >
      <g transform="translate(12 12)">
        {Array.from({ length: BAR_COUNT }, (_, i) => (
          <g key={i} transform={`rotate(${i * (360 / BAR_COUNT)})`}>
            <rect
              x="-0.95"
              y="-11"
              width="1.9"
              height="4.75"
              rx="0.95"
              ry="0.95"
              fill={fill}
              className="motion-reduce:animate-none motion-reduce:opacity-50 animate-ios-activity-bar"
              style={{
                animationDelay: `${(-i / BAR_COUNT) * durationSec}s`,
              }}
            />
          </g>
        ))}
      </g>
    </svg>
  );
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  size = 'md',
  fullScreen = false,
  statusBarBackground,
}) => {
  usePageStatusBar(
    fullScreen
      ? {
          background: statusBarBackground ?? PAGE_CHROME_LOADER_BRAND_GREEN,
          rootSafeAreaTop: '0px',
        }
      : {}
  );

  const pixelSizes = { sm: 32, md: 44, lg: 56 } as const;

  const spinner = fullScreen ? (
    <IOSLineActivitySpinner pixelSize={pixelSizes[size]} fill={SPINNER_ON_GREEN} />
  ) : (
    <IOSLineActivitySpinner pixelSize={pixelSizes[size]} fill={SPINNER_ON_DARK} />
  );

  const messageEl =
    message != null && message !== '' ? (
      <p
        className={
          fullScreen
            ? `text-center text-gray-900 max-w-[min(100vw-2rem,24rem)] ${
                size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base sm:text-lg'
              }`
            : `mt-4 text-gray-300 ${
                size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'
              }`
        }
      >
        {message}
      </p>
    ) : null;

  if (fullScreen) {
    return (
      <div
        className="min-h-screen bg-primary-400 flex flex-col items-center justify-center px-4 py-16 gap-6"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {spinner}
        {messageEl}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center">
      {spinner}
      {messageEl}
    </div>
  );
};
